import React, { useCallback, useEffect, useState } from 'react'
import {
    BigTeaserHeadlineFont,
    SmallBodyFont,
    Divider,
    NonFlexingContainer,
} from '@sport1/news-styleguide'
import { useTranslation } from 'react-i18next'
import { IBallot, IQuestion } from '../../../../api/types'
import { getVote, postVote } from '../../../../api/vote'
import { useUserState } from '../../../../providers/UserProvider/UserProvider'
import { useBallotState } from '../../../../providers/VotingProvider/VotingProvider'
import CardHeader from '../../../Shared/CardHeader'
import { usePublishFrameEvent } from '../../../../providers/ParentFrameProvider/ParentFrameProvider'
import {
    errorEvent,
    showResultEvent,
    userVotedEvent,
} from '../../../../providers/ParentFrameProvider/parentFrameEvents'
import OptionListSection from './OptionsFragment/OptionListSection'

type Props = {
    pollID: string
    question: IQuestion
    pollTitle: string
}

const QuestionFragment: React.FC<Props> = ({ pollTitle, pollID, question }) => {
    const { t } = useTranslation()
    const publish = usePublishFrameEvent()
    const user = useUserState()
    const [ballot, addBallot] = useBallotState()

    const { uuid, image, title } = question
    document.title = title
    const [loaded, setLoaded] = useState(true)

    const initFragmentState = useCallback(() => {
        return {
            question,
            voted: ballot && uuid in ballot,
            stored: (ballot && ballot[uuid]) || [],
        }
    }, [ballot, question, uuid])

    const [state, setFragmentState] = useState(initFragmentState())

    const updateFragmentWithBallot = useCallback(
        (nextState, nextBallot) => {
            setFragmentState({
                ...nextState,
                voted: uuid && uuid in nextBallot,
                stored: (nextBallot && nextBallot[uuid]) || [],
            })
        },
        [uuid]
    )

    useEffect(() => {
        if (state.voted && !state.question.votesCount) {
            setLoaded(false)
            getVote(uuid)
                .then(fetchedQuestion => {
                    if (fetchedQuestion && fetchedQuestion.data) {
                        setFragmentState({
                            ...state,
                            question: fetchedQuestion.data as IQuestion,
                        })
                        publish(showResultEvent())
                    }
                })
                .finally(() => {
                    setLoaded(true)
                })
        }
    }, [publish, uuid, state])

    const onVote = async (voteUuid: string) => {
        setLoaded(false)
        const voteData: IBallot = {
            anonymousUser: user.uuid,
            poll: pollID,
            question: uuid,
            options: [voteUuid],
        }
        try {
            const fetchedQuestion = await postVote(voteData)
            if (fetchedQuestion && fetchedQuestion.data) {
                const storage = addBallot(voteData)
                updateFragmentWithBallot(
                    {
                        ...state,
                        question: fetchedQuestion.data as IQuestion,
                    },
                    storage
                )
                publish(userVotedEvent({ title: pollTitle, uuid: pollID, type: 'poll' }))
            }
        } catch (error) {
            publish(errorEvent())
        } finally {
            setLoaded(true)
        }
    }

    return (
        <>
            <CardHeader text={t('header')} imageUrl={image} />
            <NonFlexingContainer marginY={3}>
                <BigTeaserHeadlineFont>{title}</BigTeaserHeadlineFont>
            </NonFlexingContainer>
            <NonFlexingContainer marginY={2}>
                <OptionListSection
                    options={state.question.options}
                    onVote={onVote}
                    votesCount={state.question.votesCount}
                    disable={state.voted || !loaded}
                    votesStored={state.stored}
                />
            </NonFlexingContainer>
            <NonFlexingContainer marginY={3}>
                <Divider />
            </NonFlexingContainer>
            {state.voted && state.question.votesCount ? (
                <SmallBodyFont textAlign="right">
                    {`${state.question.votesCount} ${t('votes')}`}
                </SmallBodyFont>
            ) : null}
        </>
    )
}

export default QuestionFragment
